import { useLazyAccessCheckQuery } from "app/services/api/authorisationApi";
import { useActivityDetection } from "ui/hooks/useActivityDetection";
import useLogout from "./useLogout";
import { useDispatch, useSelector } from "react-redux";
import { hideAuthModal, showAuthModal } from "app/store/slices/authModal";
import { RootState } from "../../../services/store";
import { useEffect, useRef } from "react";
import { SessionService } from "app/services/session/SessionService";

/**
 * Centralized Session Management Hook
 *
 * Replaces useSessionKeepAlive with cleaner separation of concerns.
 * All business logic is delegated to SessionService.
 */
export function useSessionManager() {
  const [trigger] = useLazyAccessCheckQuery();
  const { logout } = useLogout();
  const dispatch = useDispatch();
  const lastAccessCheckRef = useRef<number>(0);

  // Get session state from Redux
  const isLoggedIn = useSelector(
    (state: RootState) => !!state.authorisation?.auth?.token,
  );
  const userId = useSelector((state: RootState) =>
    state.authorisation?.user?.id?.toString(),
  );

  const authModalState = useSelector((state: RootState) => state.authModal!);
  const sessionState = {
    lastLoginTimestamp: authModalState.lastLoginTimestamp,
    sessionWarningShown: authModalState.sessionWarningShown,
    isLoggedIn,
  };

  // Initialize session analytics on mount
  useEffect(() => {
    SessionService.initializeSession(userId);
  }, [userId]);

  // Track login events when lastLoginTimestamp changes
  useEffect(() => {
    if (authModalState.lastLoginTimestamp) {
      const timeSinceLogin = Date.now() - authModalState.lastLoginTimestamp;
      // Only track if this is a recent login (within 1 second) to avoid tracking on page refresh
      if (timeSinceLogin < 1000) {
        SessionService.trackLogin("initial", userId);
      }
    }
  }, [authModalState.lastLoginTimestamp, userId]);

  const { resetActivity } = useActivityDetection({
    debounceTime: 5 * 1000,
    checkInterval: 1 * 60 * 1000,

    onIntervalActivity: async () => {
      // Delegate debouncing logic to SessionService
      if (
        SessionService.shouldDebounceAccessCheck(lastAccessCheckRef.current)
      ) {
        return;
      }
      lastAccessCheckRef.current = Date.now();

      await trigger()
        .unwrap()
        .catch(() => {
          // Modal decision and tracking handled by SessionService
          if (SessionService.shouldShowModalFor401(sessionState)) {
            dispatch(showAuthModal());
          }
        });
    },

    onIntervalNoActivity: ({ durationInMs }) => {
      if (!isLoggedIn) return;

      // Delegate timeout logic to SessionService
      if (SessionService.shouldFullLogout(durationInMs)) {
        dispatch(hideAuthModal());
        SessionService.trackLogout("timeout");
        logout();
      } else if (
        SessionService.shouldShowReAuthModal(
          durationInMs,
          sessionState.sessionWarningShown,
        )
      ) {
        SessionService.trackInactivityModal(durationInMs);
        dispatch(showAuthModal());
      }
    },
  });

  // Reset activity on login
  useEffect(() => {
    if (sessionState.lastLoginTimestamp) {
      resetActivity();
    }
  }, [sessionState.lastLoginTimestamp, resetActivity]);

  // Expose session utilities for other components
  return {
    sessionState,
    isInCooldown: () =>
      SessionService.isInCooldown(sessionState.lastLoginTimestamp),
    getDebugInfo: (inactivityDuration?: number) =>
      SessionService.getDebugInfo(sessionState, inactivityDuration),
    trackLogout: (reason: "user_action" | "timeout" | "401") =>
      SessionService.trackLogout(reason),
    trackLogin: (source: "initial" | "reauth") =>
      SessionService.trackLogin(source, userId),
  };
}
